import axios, { AxiosInstance } from "axios";
import axiosRetry from "axios-retry";

/** Normalise curriculum-provided HTML/text into clean, markdown-ish plain text. */
export function sanitizeGoalContent(content: string): string {
	return (content ?? '')
		// line breaks
		.replace(/(\s*)<br\s*\/?>/gi, '\n')
		// bullets to markdown
		.replace(/•(\s*)/g, '- ')
		// list items to markdown (allow attributes + multiline content)
		.replace(/<li[^>]*>(.*?)<\/li>/gis, (match, p1) => `\n- ${p1}`)
		// block-level closing tags become line breaks
		.replace(/<\/(p|div|h[1-6])>/gi, '\n')
		// strip any remaining tags
		.replace(/<[^>]+>/g, ' ')
		// tidy whitespace
		.replace(/[ \t]+/g, ' ')
		.replace(/ ?\n ?/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

export abstract class AbstractGoalLoader {

	/** Create an axios client with one retry and exponential backoff. */
	protected createRetryingClient(baseURL: string): AxiosInstance {
		const client = axios.create({ baseURL });
		axiosRetry(client, {
			retries: 1,
			retryDelay: axiosRetry.exponentialDelay,
		});
		return client;
	}

	/** Return the last path segment of a URL/href (e.g. "/content/uuid" -> "uuid"). */
	protected extractIdFromUrl(url: string): string {
		const parts = url.split('/');
		return parts[parts.length - 1];
	}

	/** Normalise curriculum-provided HTML/text into clean, markdown-ish plain text. */
	protected sanitizeContent(content: string): string {
		return sanitizeGoalContent(content);
	}

}

/** Keyed promise memoization, reusable at whatever scope a loader needs. */
export class PromiseCache<T> {

	private entries = new Map<string, Promise<T>>();

	/** Return the cached promise for `key`, or create + store it via `factory`. */
	getOrCreate(key: string, factory: () => Promise<T>): Promise<T> {
		let promise = this.entries.get(key);
		if (!promise) {
			promise = factory();
			this.entries.set(key, promise);
			// Do not cache failures: evict a rejected promise so the next call retries.
			promise.catch(() => {
				if (this.entries.get(key) === promise) {
					this.entries.delete(key);
				}
			});
		}
		return promise;
	}

	has(key: string): boolean {
		return this.entries.has(key);
	}

	clear(): void {
		this.entries.clear();
	}

}
